import * as self from '@/utils/print_measurement_results'
import moment from 'moment'
import { createPdf } from 'pdfmake/build/pdfmake.min.js'
import {
  makeFooter,
  makePageSize,
  makeOwnerPatientText,
  makeSignatureText
} from '@/utils/print_utils'

export const makeValueCell = value => {
  if (value.includes('↓')) {
    return { text: value, color: '#0000B0' }
  } else if (value.includes('↑')) {
    return { text: value, color: '#B00000' }
  } else {
    return { text: value }
  }
}

export const makeTableWidths = measurementResultLength => {
  if (measurementResultLength === 1) {
    return ['30%', '12%', '8%', '10%', '40%']
  } else if (measurementResultLength === 2) {
    return ['25%', '10%', '10%', '8%', '10%', '36%']
  } else {
    return ['20%', '9%', '9%', '9%', '8%', '10%', '35%']
  }
}

export const makeContentTableHeader = measurementResultsToPrint => {
  const resultCells = measurementResultsToPrint.map(measurementResult => {
    const date = measurementResult.date
    const dateText = moment(date, 'YYYYMMDD').format('YYYY/MM/DD')
    const cell = {
      stack: [
        { text: dateText, fontSize: 7 },
        { text: measurementResult.categoryName }
      ]
    }
    return cell
  })
  const baseCell = { margin: [0, 3, 0, 3], fontSize: 10 }
  const cells = [
    { text: '検査項目', ...baseCell },
    ...resultCells,
    { text: '単位', ...baseCell },
    { text: '基準値', ...baseCell },
    { text: '説明', ...baseCell }
  ]
  return cells
}

export const makeContentTableBodyRow = measurementResultItemToPrint => {
  const {
    itemJaName,
    values,
    unit,
    range,
    description
  } = measurementResultItemToPrint
  const nameCell = { text: itemJaName, alignment: 'left' }
  const valueCells = values.map(value => makeValueCell(value))
  const unitCell = { text: unit }
  const rangeCell = { text: range }
  const descriptionCell = { text: description, alignment: 'left' }
  const cells = [nameCell, ...valueCells, unitCell, rangeCell, descriptionCell]
  return cells
}

export const makeContentHeader = ({
  clinic,
  clinicImage,
  showClinicName,
  owner,
  patient,
  speciesName
}) => {
  const ratio = 1
  const ownerPatientTable = makeOwnerPatientText({
    owner,
    ownerShowIdFlg: true,
    patient,
    patientShowIdFlg: true,
    speciesName
  })
  const signatureParagraphs = makeSignatureText({
    ratio,
    clinic,
    clinicImage,
    showClinicName
  })
  // 右端の医院署名のどれかの文字数が長い時に「検査結果」の文字が改行されないよう width を設定。
  // width は columns と組み合わせて使用しないと反映されないようなので下記のような書き方をしている。
  const titleWidth = 260
  const titleParagraph = { text: '検査結果', fontSize: 40, width: titleWidth }
  return {
    columns: [
      {
        stack: [titleParagraph, ownerPatientTable],
        width: '*'
      },
      {
        stack: signatureParagraphs,
        width: 'auto',
        margin: [10, 0, 0, 0]
      }
    ],
    margin: [0, 0, 0, 10]
  }
}

export const makeContentTable = (
  measurementResultsToPrint,
  measurementResultItemsToPrint
) => {
  const widths = makeTableWidths(measurementResultsToPrint.length)
  const contentTableHeader = makeContentTableHeader(measurementResultsToPrint)
  const contentTableBody = measurementResultItemsToPrint.map(item => {
    return makeContentTableBodyRow(item)
  })
  const measurementResultTable = {
    alignment: 'center',
    fontSize: 8,
    layout: 'measurementResultTable',
    table: {
      headerRows: 1,
      widths,
      body: [contentTableHeader, ...contentTableBody],
      dontBreakRows: true
    },
    width: 'auto'
  }
  return measurementResultTable
}

export const makeDocDefinition = ({
  clinic,
  clinicImage,
  showClinicName,
  owner,
  patient,
  speciesName,
  printData
}) => {
  const contentHeader = self.makeContentHeader({
    clinic,
    clinicImage,
    showClinicName,
    owner,
    patient,
    speciesName
  })
  const contentTable = self.makeContentTable(
    printData.measurementResultsToPrint,
    printData.measurementResultItemsToPrint
  )
  const content = [contentHeader, contentTable]
  return {
    content,
    defaultStyle: {
      font: 'NotoSansMono',
      fontSize: 12,
      color: '#3e3e3e',
      preserveLeadingSpaces: true
    },
    footer: (currentPage, pageCount) =>
      makeFooter({
        currentPage,
        fontSize: 11,
        pageCount
      }),
    pageMargins: [20, 20, 20, 41],
    pageOrientation: 'portrait',
    pageSize: makePageSize('A4')
  }
}

// args = {
//   clinic,
//   clinicImage,
//   showClinicName,
//   owner,
//   patient,
//   speciesName,
//   printData
// }
export const printMeasurementResults = args => {
  const docDefinition = self.makeDocDefinition(args)
  const fonts = {
    NotoSansMono: {
      normal: 'NotoSansMonoCJKJPRegular.otf',
      bold: 'NotoSansMonoCJKJPRegular.otf',
      italics: 'NotoSansMonoCJKJPRegular.otf',
      bolditalics: 'NotoSansMonoCJKJPRegular.otf'
    }
  }
  const tableLayouts = {
    measurementResultTable: {
      paddingLeft: () => 1,
      paddingRight: () => 1,
      hLineColor: '#3e3e3e',
      vLineColor: '#3e3e3e',
      fillColor: (rowIndex, node, colIndex) =>
        rowIndex === 0 ? '#f0f0f0' : null
    }
  }
  createPdf(docDefinition, tableLayouts, fonts).open()
}
